/**
 * Blockchain Dashboard - Hotel Owner Module
 * Integrated blockchain features for hotel administration
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import {
  Wallet,
  Shield,
  Gauge,
  Flame,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Copy,
  ExternalLink,
  Activity,
  Settings,
  ArrowLeft,
} from 'lucide-react';

// Import blockchain API client and utilities
import {
  getSystemHealth,
  getHotelFeatures,
  toggleHotelFeature,
  calculateHotelScore,
  runMonthlyScoring,
} from '@/lib/blockchainApi';
import {
  formatAddress,
  formatHermes,
  formatScore,
  getTierFromScore,
  mapBackendTierToFrontend,
  getTierColorClasses,
  copyToClipboard,
} from '@/utils/blockchainFormat';

// Import new blockchain components
import { HotelTierBadge } from '@/components/blockchain/HotelTierBadge';
import { WalletAddressesCard } from '@/components/blockchain/WalletAddressesCard';
import { AgentChatSection } from '@/components/blockchain/AgentChatSection';
import { BACKEND_URL, API } from '@/lib/backendUrl';

export default function BlockchainDashboardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // UI State
  const [hotelId, setHotelId] = useState('');
  const [currentHotelId, setCurrentHotelId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [systemHealthLoading, setSystemHealthLoading] = useState(true);
  const [autoLoadAttempted, setAutoLoadAttempted] = useState(false);

  // Data State
  const [systemHealth, setSystemHealth] = useState(null);
  const [hotelFeatures, setHotelFeatures] = useState(null);
  const [scoreData, setScoreData] = useState(null);
  const [blockchainHotel, setBlockchainHotel] = useState(null); // NEW: Full blockchain hotel data

  // Check if accessed from hotel owner dashboard (has hotelId in URL)
  const isHotelOwnerView = Boolean(searchParams.get('hotelId'));

  // Auto-load hotel data from URL params
  useEffect(() => {
    const hotelIdFromUrl = searchParams.get('hotelId');
    if (hotelIdFromUrl && !autoLoadAttempted) {
      setHotelId(hotelIdFromUrl);
      setAutoLoadAttempted(true);
      // Trigger auto-load
      loadHotelDataById(hotelIdFromUrl);
    }
  }, [searchParams, autoLoadAttempted]);

  // Load system health on mount
  useEffect(() => {
    loadSystemHealth();
  }, []);

  // ============================================
  // API CALLS
  // ============================================

  const loadSystemHealth = async () => {
    try {
      setSystemHealthLoading(true);
      const data = await getSystemHealth();
      setSystemHealth(data);
      toast.success('System health check complete');
    } catch (error) {
      console.error('Health check failed:', error);
      toast.error('Failed to connect to blockchain backend');
      setSystemHealth({ status: 'error', message: error.message });
    } finally {
      setSystemHealthLoading(false);
    }
  };

  const loadHotelDataById = async (targetHotelId) => {
    if (!targetHotelId || !targetHotelId.trim()) {
      toast.error('Please enter a hotel ID');
      return;
    }

    try {
      setLoading(true);
      setCurrentHotelId(targetHotelId);

      // Load features
      const features = await getHotelFeatures(targetHotelId);
      setHotelFeatures(features);

      // Load full blockchain hotel data
      try {
        const backendUrl = BACKEND_URL;
        const detailResponse = await fetch(`${backendUrl}/api/blockchain/hotels/${targetHotelId}`);
        
        if (detailResponse.ok) {
          const detailData = await detailResponse.json();
          if (detailData.enrolled && detailData.hotel) {
            // Normalize wallet data structure
            const normalizedHotel = { ...detailData.hotel };
            
            // Check if wallets are stored flat (treasury_wallet, bitsy_wallet, etc.)
            if (!normalizedHotel.wallets && normalizedHotel.treasury_wallet) {
              normalizedHotel.wallets = {
                treasury: normalizedHotel.treasury_wallet,
                bitsy_agent: normalizedHotel.bitsy_wallet,
                marina_agent: normalizedHotel.marina_wallet,
                sage_agent: normalizedHotel.sage_wallet
              };
            }
            
            setBlockchainHotel(normalizedHotel);
          }
        }
      } catch (error) {
        console.error('Failed to load blockchain hotel data:', error);
        // Non-fatal, continue with features data
      }

      toast.success(`Loaded data for ${features.hotel_name || targetHotelId.substring(0, 8) + '...'}`);
    } catch (error) {
      console.error('Failed to load hotel data:', error);
      toast.error(error.message || 'Failed to load hotel data');
      setHotelFeatures(null);
      setScoreData(null);
      setBlockchainHotel(null);
    } finally {
      setLoading(false);
    }
  };

  const loadHotelData = async () => {
    await loadHotelDataById(hotelId);
  };

  const handleToggleFeature = async (featureId) => {
    if (!currentHotelId) return;

    try {
      const result = await toggleHotelFeature(currentHotelId, featureId);
      
      // Update local state - features is an object, not array
      setHotelFeatures((prev) => ({
        ...prev,
        features: {
          ...prev.features,
          [featureId]: {
            ...prev.features[featureId],
            enabled: result.enabled
          }
        }
      }));

      toast.success(`Feature ${result.enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Failed to toggle feature:', error);
      toast.error('Failed to toggle feature');
    }
  };

  const handleCalculateScore = async () => {
    if (!currentHotelId) {
      toast.error('Please load a hotel first');
      return;
    }

    try {
      setLoading(true);
      const result = await calculateHotelScore(currentHotelId);
      // Backend returns {success: true, data: {...}}, extract the data
      setScoreData(result.data || result);
      toast.success('Score calculated successfully');
    } catch (error) {
      console.error('Failed to calculate score:', error);
      toast.error(error.message || 'Failed to calculate score');
    } finally {
      setLoading(false);
    }
  };

  const handleRunMonthly = async () => {
    try {
      setLoading(true);
      const result = await runMonthlyScoring();
      toast.success(`Monthly scoring complete! Processed ${result.hotels_processed || 0} hotels`);
    } catch (error) {
      console.error('Failed to run monthly scoring:', error);
      toast.error(error.message || 'Failed to run monthly scoring');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // RENDER HELPERS
  // ============================================

  const renderSystemHealth = () => {
    if (systemHealthLoading) {
      return (
        <Card data-testid="system-health-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              System Health
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </CardContent>
        </Card>
      );
    }

    if (!systemHealth) return null;

    const isHealthy = systemHealth.status === 'healthy';

    return (
      <Card data-testid="system-health-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            System Health
            {isHealthy ? (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-500" />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Backend</span>
              <Badge variant={isHealthy ? 'default' : 'destructive'}>
                {isHealthy ? 'Online' : 'Offline'}
              </Badge>
            </div>
            {systemHealth.blockchain_connected !== undefined && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Blockchain</span>
                <Badge variant={systemHealth.blockchain_connected ? 'default' : 'destructive'}>
                  {systemHealth.blockchain_connected ? 'Connected' : 'Disconnected'}
                </Badge>
              </div>
            )}
            {systemHealth.event_indexer_running !== undefined && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Event Indexer</span>
                <Badge variant={systemHealth.event_indexer_running ? 'default' : 'secondary'}>
                  {systemHealth.event_indexer_running ? 'Running' : 'Offline'}
                </Badge>
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadSystemHealth}
            className="w-full mt-4"
            data-testid="refresh-health-button"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </CardContent>
      </Card>
    );
  };

  const renderHotelSelector = () => {
    return (
      <Card data-testid="hotel-selector-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Select Hotel
          </CardTitle>
          <CardDescription>
            Enter a hotel ID to load blockchain data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="hotel-id-input">Hotel ID</Label>
              <Input
                id="hotel-id-input"
                data-testid="hotel-id-input"
                value={hotelId}
                onChange={(e) => setHotelId(e.target.value)}
                placeholder="Enter hotel ID (UUID or ObjectId)"
                className="mono"
              />
            </div>
            <Button
              onClick={loadHotelData}
              disabled={loading || !hotelId.trim()}
              className="w-full"
              data-testid="load-hotel-button"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4 mr-2" />
                  Load Hotel Data
                </>
              )}
            </Button>
            {currentHotelId && (
              <div className="mt-2 p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Current Hotel:</p>
                <p className="text-sm mono font-medium">{formatAddress(currentHotelId, 8, 8)}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderFeatureToggles = () => {
    if (!hotelFeatures) return null;

    // Convert features object to array
    const featuresArray = Object.entries(hotelFeatures.features || {}).map(([id, feature]) => ({
      id,
      name: feature.name || id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      description: feature.description || 'No description available',
      enabled: feature.enabled || false,
      requires_restart: feature.requires_restart || false
    }));

    return (
      <Card data-testid="feature-toggles-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Feature Management
          </CardTitle>
          <CardDescription>
            All {featuresArray.length} blockchain features for this hotel
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
            {featuresArray.map((feature) => (
              <div
                key={feature.id}
                className="flex items-start justify-between gap-4 py-3 border-b last:border-0"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Label htmlFor={`feature-${feature.id}`} className="font-medium">
                      {feature.name}
                    </Label>
                    {feature.requires_restart && (
                      <Badge variant="outline" className="text-xs">
                        Restart Required
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {feature.description}
                  </p>
                </div>
                <Switch
                  id={`feature-${feature.id}`}
                  data-testid={`feature-toggle-${feature.id}`}
                  checked={feature.enabled}
                  onCheckedChange={() => handleToggleFeature(feature.id)}
                  aria-label={`Toggle ${feature.name}`}
                />
              </div>
            ))}
          </div>
          
          {currentHotelId && (
            <div className="pt-4 mt-4 border-t">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate(`/admin/blockchain/features/${currentHotelId}`)}
                data-testid="view-full-features-button"
              >
                <Settings className="h-4 w-4 mr-2" />
                View Full Feature Management
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderScoring = () => {
    return (
      <Card data-testid="scoring-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gauge className="h-5 w-5" />
            Scoring & Tiers
          </CardTitle>
          <CardDescription>
            Calculate quality-based tier score
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Button
              onClick={handleCalculateScore}
              disabled={loading || !currentHotelId}
              className="w-full"
              data-testid="calculate-score-button"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  <Gauge className="h-4 w-4 mr-2" />
                  Calculate Score
                </>
              )}
            </Button>

            {scoreData && (
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Final Score:</span>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl heading-font tabular-nums">
                      {formatScore(scoreData.final_score / 10)}/100
                    </span>
                    <Badge className={getTierColorClasses(mapBackendTierToFrontend(scoreData.tier))}>
                      {mapBackendTierToFrontend(scoreData.tier)}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Quality Score:</span>
                  <span className="text-lg tabular-nums">{formatScore(scoreData.quality_score / 10)}/100</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Value Score:</span>
                  <span className="text-lg tabular-nums">{formatScore(scoreData.value_score / 10)}/100</span>
                </div>
              </div>
            )}

            {/* Platform Admin Only: Run Monthly Scoring */}
            {!isHotelOwnerView && (
              <div className="pt-4 border-t">
                <Button
                  onClick={handleRunMonthly}
                  disabled={loading}
                  variant="outline"
                  className="w-full"
                  data-testid="run-monthly-button"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <Flame className="h-4 w-4 mr-2" />
                      Run Monthly Scoring (All Hotels)
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Calculates scores, applies rewards & burns for all hotels
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-30">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {isHotelOwnerView && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate(-1)}
                  data-testid="back-button"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              )}
              <div>
                <h1 className="text-3xl heading-font tracking-tight">
                  Blockchain Dashboard
                  {!isHotelOwnerView && <Badge variant="secondary" className="ml-2">Admin</Badge>}
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {isHotelOwnerView 
                    ? `${hotelFeatures?.hotel_name || 'Your Hotel'} - Web3 Integration`
                    : 'Hotel blockchain integration management'
                  }
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={loadSystemHealth}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh All
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Tier Badge - Only for hotel owner view */}
        {isHotelOwnerView && blockchainHotel && blockchainHotel.tier_scores && (
          <div className="mb-6">
            <HotelTierBadge 
              tierData={blockchainHotel.tier_scores}
              hotelName={hotelFeatures?.hotel_name || 'Your Hotel'}
            />
          </div>
        )}

        {/* Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* System Health - Hide in hotel owner view */}
            {!isHotelOwnerView && renderSystemHealth()}
            
            {/* Hotel Selector - Hide when hotelId in URL */}
            {!isHotelOwnerView && renderHotelSelector()}
            
            {/* Wallet Addresses - Hotel owner view only */}
            {isHotelOwnerView && blockchainHotel && (
              <WalletAddressesCard 
                wallets={blockchainHotel.wallets}
                stakingData={blockchainHotel.staking_data}
              />
            )}
            
            {renderFeatureToggles()}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {renderScoring()}
            
            {/* AI Agents Chat - Hotel owner view only */}
            {isHotelOwnerView && currentHotelId && (
              <AgentChatSection hotelId={currentHotelId} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
